/** @format */

import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../store/store";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";

const axiosInstance = axios.create({
	baseURL: API_BASE_URL,
	withCredentials: true,
});

// ==================== Types ====================

export interface SuspensionCircleDetail {
	circleId: string;
	circleName: string;
	missedDates: string[];
	missedAmountCents: number;
	lateFeesCents: number;
	currency?: string; // ISO 4217 currency code for this circle
}

export interface OutstandingDues {
	userId: string;
	email: string;
	firstName: string;
	lastName: string;
	missedContributionsCents: number;
	lateFeesCents: number;
	totalDueCents: number;
	affectedCircles: SuspensionCircleDetail[];
	hasOutstandingDues: boolean;
	eligibleForAutoSuspension: boolean;
	payoutProcessedWithoutPayment: boolean;
	currency?: string; // ISO 4217 currency code (dominant circle currency)
	lastPayoutId?: string;
	lastPayoutDate?: string;
}

export interface SuspensionStatus {
	isSuspended: boolean;
	banType?: string | null;
	banReason?: string | null;
	bannedAt?: string | null;
	suspensionDetails?: {
		missedContributionsCents: number;
		lateFeesCents: number;
		totalDueCents: number;
		affectedCircles: SuspensionCircleDetail[];
	};
	canSelfResolve: boolean;
	outstandingDues?: OutstandingDues;
}

export interface PaymentResult {
	circleId: string;
	circleName: string;
	success: boolean;
	paidCents?: number;
	error?: string;
}

export interface PayAllDuesResponse {
	success: boolean;
	message: string;
	data: {
		totalPaidCents: number;
		paymentResults: PaymentResult[];
		suspensionLifted: boolean;
		remainingDues?: OutstandingDues | null;
	};
}

interface OutstandingDuesState {
	suspensionStatus: SuspensionStatus | null;
	loading: boolean;
	error: string | null;
	paymentProcessing: boolean;
	paymentSuccess: boolean;
	paymentError: string | null;
	paymentResult: PayAllDuesResponse | null;
}

const initialState: OutstandingDuesState = {
	suspensionStatus: null,
	loading: false,
	error: null,
	paymentProcessing: false,
	paymentSuccess: false,
	paymentError: null,
	paymentResult: null,
};

// ==================== Async Thunks ====================

/**
 * Fetch current user's suspension status and outstanding dues
 */
export const fetchMySuspensionStatus = createAsyncThunk<
	SuspensionStatus,
	void,
	{ rejectValue: string }
>("outstandingDues/fetchMySuspensionStatus", async (_, { rejectWithValue }) => {
	try {
		const response = await axiosInstance.get(
			"/outstanding-dues/me/suspension-status",
		);
		return response.data.data;
	} catch (error: any) {
		const message =
			error.response?.data?.message ||
			error.message ||
			"Failed to fetch suspension status";
		return rejectWithValue(message);
	}
});

/**
 * Fetch current user's outstanding dues
 */
export const fetchMyOutstandingDues = createAsyncThunk<
	OutstandingDues,
	void,
	{ rejectValue: string }
>("outstandingDues/fetchMyOutstandingDues", async (_, { rejectWithValue }) => {
	try {
		const response = await axiosInstance.get("/outstanding-dues/me");
		return response.data.data;
	} catch (error: any) {
		const message =
			error.response?.data?.message ||
			error.message ||
			"Failed to fetch outstanding dues";
		return rejectWithValue(message);
	}
});

/**
 * Pay all outstanding dues to lift suspension
 */
export const payAllOutstandingDues = createAsyncThunk<
	PayAllDuesResponse,
	string | undefined,
	{ rejectValue: string }
>(
	"outstandingDues/payAllOutstandingDues",
	async (paymentMethodId, { rejectWithValue }) => {
		try {
			const response = await axiosInstance.post("/outstanding-dues/pay-all", {
				paymentMethodId,
			});
			return response.data;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to process payment";
			return rejectWithValue(message);
		}
	},
);

/**
 * Pay outstanding dues for a specific circle
 */
export const payCircleOutstandingDues = createAsyncThunk<
	{
		success: boolean;
		message: string;
		data: {
			circleId: string;
			circleName: string;
			paidCents: number;
			remainingDues?: OutstandingDues | null;
			suspensionLifted: boolean;
		};
	},
	{ circleId: string; paymentMethodId?: string },
	{ rejectValue: string }
>(
	"outstandingDues/payCircleOutstandingDues",
	async ({ circleId, paymentMethodId }, { rejectWithValue }) => {
		try {
			const response = await axiosInstance.post(
				`/outstanding-dues/pay-circle/${circleId}`,
				{ paymentMethodId },
			);
			return response.data;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to process payment";
			return rejectWithValue(message);
		}
	},
);

// ==================== Slice ====================

const outstandingDuesSlice = createSlice({
	name: "outstandingDues",
	initialState,
	reducers: {
		clearOutstandingDuesState: (state) => {
			state.error = null;
			state.paymentError = null;
			state.paymentSuccess = false;
			state.paymentResult = null;
		},
		clearPaymentState: (state) => {
			state.paymentProcessing = false;
			state.paymentSuccess = false;
			state.paymentError = null;
			state.paymentResult = null;
		},
	},
	extraReducers: (builder) => {
		// Fetch suspension status
		builder
			.addCase(fetchMySuspensionStatus.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(
				fetchMySuspensionStatus.fulfilled,
				(state, action: PayloadAction<SuspensionStatus>) => {
					state.loading = false;
					state.suspensionStatus = action.payload;
				},
			)
			.addCase(fetchMySuspensionStatus.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload || "Failed to fetch suspension status";
			});

		// Fetch outstanding dues
		builder
			.addCase(fetchMyOutstandingDues.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(
				fetchMyOutstandingDues.fulfilled,
				(state, action: PayloadAction<OutstandingDues>) => {
					state.loading = false;
					if (state.suspensionStatus) {
						state.suspensionStatus.outstandingDues = action.payload;
					}
				},
			)
			.addCase(fetchMyOutstandingDues.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload || "Failed to fetch outstanding dues";
			});

		// Pay all outstanding dues
		builder
			.addCase(payAllOutstandingDues.pending, (state) => {
				state.paymentProcessing = true;
				state.paymentError = null;
				state.paymentSuccess = false;
			})
			.addCase(
				payAllOutstandingDues.fulfilled,
				(state, action: PayloadAction<PayAllDuesResponse>) => {
					state.paymentProcessing = false;
					state.paymentSuccess = action.payload.success;
					state.paymentResult = action.payload;

					// If suspension was lifted, update the status
					if (action.payload.data.suspensionLifted && state.suspensionStatus) {
						state.suspensionStatus.isSuspended = false;
						state.suspensionStatus.canSelfResolve = false;
						state.suspensionStatus.banType = null;
						state.suspensionStatus.banReason = null;
					}
				},
			)
			.addCase(payAllOutstandingDues.rejected, (state, action) => {
				state.paymentProcessing = false;
				state.paymentError = action.payload || "Payment failed";
			});

		// Pay circle outstanding dues
		builder
			.addCase(payCircleOutstandingDues.pending, (state) => {
				state.paymentProcessing = true;
				state.paymentError = null;
			})
			.addCase(payCircleOutstandingDues.fulfilled, (state, action) => {
				state.paymentProcessing = false;

				// Update outstanding dues if returned
				if (action.payload.data.remainingDues && state.suspensionStatus) {
					state.suspensionStatus.outstandingDues =
						action.payload.data.remainingDues;
				}

				// If suspension was lifted, update the status
				if (action.payload.data.suspensionLifted && state.suspensionStatus) {
					state.suspensionStatus.isSuspended = false;
					state.suspensionStatus.canSelfResolve = false;
					state.paymentSuccess = true;
				}
			})
			.addCase(payCircleOutstandingDues.rejected, (state, action) => {
				state.paymentProcessing = false;
				state.paymentError = action.payload || "Payment failed";
			});
	},
});

// ==================== Selectors ====================

export const selectSuspensionStatus = (state: RootState) =>
	state.outstandingDues.suspensionStatus;

export const selectOutstandingDues = (state: RootState) =>
	state.outstandingDues.suspensionStatus?.outstandingDues;

export const selectOutstandingDuesLoading = (state: RootState) =>
	state.outstandingDues.loading;

export const selectOutstandingDuesError = (state: RootState) =>
	state.outstandingDues.error;

export const selectPaymentProcessing = (state: RootState) =>
	state.outstandingDues.paymentProcessing;

export const selectPaymentSuccess = (state: RootState) =>
	state.outstandingDues.paymentSuccess;

export const selectPaymentError = (state: RootState) =>
	state.outstandingDues.paymentError;

export const selectPaymentResult = (state: RootState) =>
	state.outstandingDues.paymentResult;

export const selectCanSelfResolve = (state: RootState) =>
	state.outstandingDues.suspensionStatus?.canSelfResolve || false;

// ==================== Exports ====================

export const { clearOutstandingDuesState, clearPaymentState } =
	outstandingDuesSlice.actions;

export default outstandingDuesSlice.reducer;
